import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Button, Dialog, InputText } from "iuvity-ui-components";
import { CustomInput } from "../inputs/CustomInput";
import { DialogProps } from "../dialog/CustomDialog";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { QuickLinksFormSchema } from "../../validations/schemas";
import { QUICKLINKS_TYPE_OPTIONS } from "../../constants/index";
import { AppContext } from "../../context";

interface QuickLinkCreatorProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  createFn: Function;
  reloadFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
  options: any;
}

export const quickLinkCreatorDefaultValues = {
  name: "",
  description: "",
  labelkey: "",
  type: "",
  iconname: "",
  linkto: "",
  isexternallink: false,
  issecuredurl: false,
  fieldorder: "",
  category: "",
  status: "INACTIVE",
  releaseversion: "",
};

export const QuickLinkCreator: React.FC<QuickLinkCreatorProps> = ({
  visible,
  setVisible,
  createFn,
  reloadFn,
  auxModalController,
  options,
}) => {
  const {
    t,
    appState: { user },
  } = useContext(AppContext);

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: quickLinkCreatorDefaultValues,
  });

  const onCancel = () => {
    setVisible(false);
    reset(quickLinkCreatorDefaultValues);
  };

  const onCreateQuickLink = async () => {
    setVisible(false);
    const formValues = getValues();
    const valuesToSend = Object.keys(formValues)
    .filter((key) => (formValues as any)[key] !== "")
    .reduce((cur, key) => {
      return Object.assign(cur, { [key]: (formValues as any)[key] });
    }, {});
    const actionStatus = await createFn("CREATE", [
      {
        ...valuesToSend,
        createdby: user?.name,
      },
    ]);
    if (actionStatus === "SUCCESS") {
      reset(quickLinkCreatorDefaultValues);
      reloadFn();
    }
  };

  const handleModalProps = () => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("Quicklinks.createModalTitle"),
      content: t("Quicklinks.createModalContent"),
      onConfirm: onCreateQuickLink,
    }));
  };

  const renderFooter = () => {
    return (
      <div className="flex align-items-center justify-content-flex-end">
        <Button
          label={t("Quicklinks.cancelBtnLabel")}
          onClick={onCancel}
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={t("Quicklinks.createBtnLabel")}
          variant="p-button"
          onClick={handleModalProps}
          disabled={!isValid}
        />
      </div>
    );
  };

  return (
    <Dialog
      className="dialog-form"
      header={t("Quicklinks.newTitle")}
      visible={visible}
      onHide={onCancel}
      style={{ width: "25rem" }}
      footer={renderFooter}
    >
      <div className="form sm">
        {Object.keys(quickLinkCreatorDefaultValues)
          .filter((key) => key !== "releaseversion")
          .map((key) => {
            return (
              <div key={key}>
                <Controller
                  key={key}
                  name={key as any}
                  control={control}
                  rules={(QuickLinksFormSchema(t) as any)[key]}
                  render={({ field }) => {
                    const isBoolean = ["isexternallink", "status", "issecuredurl"].includes(
                      key
                    );
                    const extraProps = {
                      keyfilter: (QuickLinksFormSchema(t) as any)[key]?.keyfilter,
                      maxLength: (QuickLinksFormSchema(t) as any)[key]?.maxLength?.value
                    }
                    if (key === "status") {
                      field.value = getValues("status") === "ACTIVE";
                      field.onChange = (e) => {
                        setValue("status", e.value ? "ACTIVE" : "INACTIVE");
                      };
                    }
                    return (
                      <div className="flex flex-column">
                        {key === "type" ? (
                          <Dropdown
                            id="input-type"
                            className="dropdown mb-3"
                            label={t(`Quicklinks.typeLabel`)}
                            placeholder={t(`Quicklinks.typePlaceholder`)}
                            style={{ width: "100%" }}
                            options={QUICKLINKS_TYPE_OPTIONS(t).sort(
                              (a, b) => a.label.localeCompare(b.label)
                            )}
                            filter={false}
                            showClear={false}
                            {...field}
                          />
                        ) : (
                          key === "linkto" ?
                          <InputText
                            key={key}
                            id={`input-label-${key}`}
                            label={t(`Quicklinks.${key}Label`)}
                              placeholder={t(`Quicklinks.${key}PlaceholderLabel`)}
                            type="text"
                            autoComplete="off"
                            className={
                              // @ts-ignore
                              errors[key]?.message ? "p-invalid" : "mb-4"
                            }
                            style={{ width: "20rem" }}
                            tooltipIdRef={`tooltip-${key}`}
                            tooltipRef={{
                              content:t(`Quicklinks.linktoTooltipText`)
                            }}
                            hasTooltipRef={key === 'linkto'}
                            {...field}
                            {...extraProps}
                          /> :
                            <CustomInput
                              id={`input-${key}`}
                              className={
                                // @ts-ignore
                                errors[key]?.message ? "p-invalid" : "mb-3"
                              }
                              label={t(`Quicklinks.${key}Label`)}
                              placeholder={t(`Quicklinks.${key}PlaceholderLabel`)}
                              type={isBoolean ? "boolean" : "text"}
                              autoComplete="off"
                              {...field}
                              {...extraProps}
                            />
                        )}
                      </div>
                    );
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name={key as any}
                  //@ts-ignore
                  render={({ message }) => (
                    <small
                      className="p-error block"
                      style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                    >
                      {message}
                    </small>
                  )}
                />
              </div>
            );
          })}

        <div key="releaseversion">
          <Controller
            key="releaseversion"
            name="releaseversion"
            control={control}
            rules={(QuickLinksFormSchema(t) as any).releaseversion}
            render={({ field }) => {
              return (
                // @ts-ignore
                <Dropdown
                  id="im-type-dropdown"
                  className="dropdown mb-3"
                  label={t("Widgets.releaseversionLabel")}
                  placeholder={t("Widgets.releaseversionPlaceholder")}
                  options={options.sort((a: any, b: any) =>
                    b.label.localeCompare(a.label)
                  )}
                  filter={false}
                  showClear={false}
                  style={{
                    zIndex: "2300 !important",
                  }}
                  {...field}
                />
              );
            }}
          />
          <ErrorMessage
            errors={errors}
            name="releaseversion"
            //@ts-ignore
            render={({ message }) => (
              <small
                className="p-error block"
                style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
              >
                {message}
              </small>
            )}
          />
        </div>
      </div>
    </Dialog>
  );
};
